"use client";

// ─────────────────────────────────────────────────────────────────────────
// "Ensure this is for all such places on the UI where the old figure would
// have shown."
//
// The nav chip was fixed one surface at a time: it now says "Confirming your
// purchase…" while a purchase settles, and it hears the balance that lands
// because it listens on panda:credit-update. Nothing else did either. Settings
// → Credits, the profile stats, the pricing grid's "Current plan" lock and the
// home page's returning-user copy each read their snapshot once, on mount, and
// then held it — so a purchase completing under them left the same
// pre-purchase figure on screen that the chip used to show, with the same
// remedy: reload the page yourself.
//
// This is the general form of the chip's second half. A surface that owns a
// snapshot hands over how to re-read it, and re-reads whenever a purchase
// settles anywhere in the app — the broadcast that carries a new balance, or
// the sync latch coming down.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useRef } from "react";

import {
  CREDIT_UPDATE_EVENT,
  ENTITLEMENT_SYNC_EVENT,
} from "@/lib/billing/entitlement-refresh";

/**
 * Re-read this surface's entitlement snapshot whenever a purchase settles.
 *
 * `reload` is held in a ref, so a caller may pass an inline closure without
 * re-subscribing on every render — the listeners are attached once per mount.
 * It is called at most once per settlement: the two events fire together at the
 * end of a sync, and re-reading twice would double every request.
 */
export function useEntitlementRevalidate(reload: () => void): void {
  const latest = useRef(reload);
  useEffect(() => {
    latest.current = reload;
  }, [reload]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Coalesced to the end of the task. syncEntitlementUntilSettled broadcasts
    // credits and lowers the latch in the same tick, and a checkout return adds
    // its own broadcast a beat earlier — three signals, one re-read.
    let queued = false;
    const revalidate = () => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        latest.current();
      });
    };

    const onSyncChange = (e: Event) => {
      // Only the STOP. Re-reading as a purchase begins settling would fetch
      // exactly the pre-purchase snapshot the surface already has.
      const detail = (e as CustomEvent<{ syncing?: boolean }>).detail;
      if (detail?.syncing === false) revalidate();
    };

    window.addEventListener(CREDIT_UPDATE_EVENT, revalidate);
    window.addEventListener(ENTITLEMENT_SYNC_EVENT, onSyncChange);
    return () => {
      window.removeEventListener(CREDIT_UPDATE_EVENT, revalidate);
      window.removeEventListener(ENTITLEMENT_SYNC_EVENT, onSyncChange);
    };
  }, []);
}
